import { useAppDispatch } from 'app/hooks';
import store from 'app/store';
import { LineSetPointType } from 'data/types';
import { setConfigurations } from 'features/configurations';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import { useCallback, useMemo } from 'react';
import { linesetLevelPerimeters } from 'utils/lineset-level-perimeters';
import {
  materializeLinesetPointLevels,
  resolveLinesetPointLevels,
  resolveSegmentLevelForPointInsertion
} from 'utils/lineset-point-levels';
import { snapLinesetPoint } from 'utils/lineset-snap';

export type LinesetPointMove = {
  positionX: number;
  positionY: number;
  positionZ?: number | null;
  levelUniqueId?: string;
};

export function useLinesetActions(
  configurationId: string,
  linesetUniqueId: string
) {
  const dispatch = useAppDispatch();

  const addPoint = useCallback(
    (segmentIndex: number, x: number, y: number, z?: number | null) => {
      const { structure } = store.getState().structure;

      const updatedConfigurations = produce(
        store.getState().configurations.configurations,
        (draft) => {
          const config = draft.find((c) => c.uniqueId === configurationId);
          if (!config || !config.lineSets) return;

          const linesetIndex = config.lineSets.findIndex(
            (ls) => ls.uniqueId === linesetUniqueId
          );
          if (linesetIndex === -1) return;

          const draftLineset = config.lineSets[linesetIndex];
          const segmentLevelUniqueId = resolveSegmentLevelForPointInsertion(
            draftLineset,
            segmentIndex
          );
          const snapped = snapLinesetPoint(
            { x, y },
            linesetLevelPerimeters(structure, segmentLevelUniqueId)
          );

          const newPoint: LineSetPointType = {
            positionX: snapped.x,
            positionY: snapped.y,
            uniqueId: nanoid(5),
            lineSetUniqueId: linesetUniqueId,
            indexNumber: 0,
            ...(segmentLevelUniqueId
              ? { levelUniqueId: segmentLevelUniqueId }
              : {})
          };
          if (z !== null && z !== undefined) {
            newPoint.positionZ = z;
          }

          draftLineset.points.splice(segmentIndex + 1, 0, newPoint);
        }
      );
      dispatch(setConfigurations(updatedConfigurations));
    },
    [configurationId, linesetUniqueId, dispatch]
  );

  const deletePoint = useCallback(
    (pointUniqueId: string) => {
      const updatedConfigurations = produce(
        store.getState().configurations.configurations,
        (draft) => {
          const config = draft.find((c) => c.uniqueId === configurationId);
          if (!config || !config.lineSets) return;
          const linesetIndex = config.lineSets.findIndex(
            (ls) => ls.uniqueId === linesetUniqueId
          );
          if (linesetIndex === -1) return;
          const draftLineset = config.lineSets[linesetIndex];
          if (draftLineset.points.length <= 2) return;
          const pointIndex = draftLineset.points.findIndex(
            (p) => p.uniqueId === pointUniqueId
          );
          if (pointIndex === -1) return;
          materializeLinesetPointLevels(draftLineset);
          draftLineset.points.splice(pointIndex, 1);
        }
      );
      dispatch(setConfigurations(updatedConfigurations));
    },
    [configurationId, linesetUniqueId, dispatch]
  );

  // Line sets are not a valid-systems input, so no condenser is marked stale
  // here (that would only force a redundant full request).
  const deleteLineset = useCallback(() => {
    const currentConfigurations =
      store.getState().configurations.configurations;

    const updatedConfigurations = produce(currentConfigurations, (draft) => {
      const config = draft.find((c) => c.uniqueId === configurationId);
      if (!config || !config.lineSets) return;

      const linesetIndex = config.lineSets.findIndex(
        (ls) => ls.uniqueId === linesetUniqueId
      );
      if (linesetIndex === -1) return;
      config.lineSets.splice(linesetIndex, 1);
    });

    dispatch(setConfigurations(updatedConfigurations));
  }, [configurationId, linesetUniqueId, dispatch]);

  const movePoint = useCallback(
    (pointUniqueId: string, move: LinesetPointMove) => {
      const { structure } = store.getState().structure;
      const updatedConfigurations = produce(
        store.getState().configurations.configurations,
        (draft) => {
          const config = draft.find((c) => c.uniqueId === configurationId);
          if (!config || !config.lineSets) return;
          const linesetIndex = config.lineSets.findIndex(
            (ls) => ls.uniqueId === linesetUniqueId
          );
          if (linesetIndex === -1) return;
          const draftLineset = config.lineSets[linesetIndex];
          const pointIndex = draftLineset.points.findIndex(
            (p) => p.uniqueId === pointUniqueId
          );
          if (pointIndex === -1) return;

          const pointLevelUniqueId =
            move.levelUniqueId ??
            resolveLinesetPointLevels(draftLineset)[pointIndex];
          if (move.levelUniqueId !== undefined) {
            materializeLinesetPointLevels(draftLineset);
          }

          const point = draftLineset.points[pointIndex];
          const snapped = snapLinesetPoint(
            { x: move.positionX, y: move.positionY },
            linesetLevelPerimeters(structure, pointLevelUniqueId)
          );
          point.positionX = snapped.x;
          point.positionY = snapped.y;
          if (move.positionZ !== undefined) {
            point.positionZ =
              move.positionZ === null ? null : Math.max(0, move.positionZ);
          }
          if (move.levelUniqueId !== undefined) {
            point.levelUniqueId = move.levelUniqueId;
          }
        }
      );
      dispatch(setConfigurations(updatedConfigurations));
    },
    [configurationId, linesetUniqueId, dispatch]
  );

  return useMemo(
    () => ({ addPoint, deletePoint, deleteLineset, movePoint }),
    [addPoint, deletePoint, deleteLineset, movePoint]
  );
}
